import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

// Extends Record so normalizeSegment can inspect arbitrary LLM-generated fields.
export interface HorizontalBarSegment extends Record<string, unknown> {
  label: string;
  value: number;
  color?: string | null;
}

export interface HorizontalBarProps {
  props: {
    segments?: HorizontalBarSegment[] | null;
    showLabels: boolean | null;
    showValues: boolean | null;
    height: "sm" | "md" | "lg" | null;
  };
}

/**
 * Theme tokens from `src/styles/semantic-theme.css` (imported in `src/index.css`).
 * No hex in specs — sentiment labels (case-insensitive) map to semantic vars; others cycle `--chart-1`…`--chart-5`.
 */
const CHART_SEQUENCE = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
] as const;

/**
 * Segment fill color: `positive` / `negative` / `neutral` in the label → sentiment CSS vars;
 * otherwise `var(--chart-1)` … `var(--chart-5)` by segment index.
 */
const colorForLabel = (
  label: string | null | undefined,
  index: number,
): string => {
  const n = (label ?? "").toLowerCase();
  if (n.includes("positive")) return "var(--sentiment-positive)";
  if (n.includes("negative")) return "var(--sentiment-negative)";
  if (n.includes("neutral")) return "var(--sentiment-neutral)";
  const idx = index % CHART_SEQUENCE.length;
  return CHART_SEQUENCE[idx] as (typeof CHART_SEQUENCE)[number];
};

/**
 * Normalise a raw segment to { label, value, color }.
 * The LLM may use arbitrary field names; fall back to the first string field
 * for label and the first numeric field for value, skipping `color` so a hex
 * never becomes the label.
 */
const normalizeSegment = (
  seg: Record<string, unknown>,
): { label: string; value: number; color?: string | null } => {
  const values = Object.entries(seg)
    .filter(([key]) => key !== "color")
    .map(([, value]) => value);
  const label =
    (typeof seg.label === "string" ? seg.label : undefined) ??
    values.find((v): v is string => typeof v === "string") ??
    "";
  const rawValue =
    (typeof seg.value === "number" ? seg.value : undefined) ??
    values.find((v): v is number => typeof v === "number");
  const color = typeof seg.color === "string" ? seg.color : null;
  return { label, value: Number(rawValue) || 0, color };
};

const HorizontalBarEmpty = () => (
  <div className="flex w-full items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xs text-muted-foreground">
    No data to display
  </div>
);

const heightClassForHeight = (
  height: HorizontalBarProps["props"]["height"],
): string => {
  if (height === "md") return "h-3";
  if (height === "lg") return "h-5";
  return "h-2";
};

/**
 * Horizontal stacked bar for part-to-whole segments (e.g. sentiment mix).
 */
const HorizontalBar = ({ props }: HorizontalBarProps): ReactNode => {
  const segments = Array.isArray(props.segments)
    ? props.segments.map(normalizeSegment)
    : [];
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return <HorizontalBarEmpty />;

  const heightClass = heightClassForHeight(props.height);
  const segmentColors = segments.map(
    (seg, i) => seg.color ?? colorForLabel(seg.label, i),
  );
  const breakdownAriaLabel = segments
    .map((s) => `${s.label}: ${Math.round((s.value / total) * 100)}%`)
    .join(", ");

  return (
    <div className="flex w-full min-w-0 flex-col gap-1.5">
      <div
        className={cn("flex w-full overflow-hidden rounded-full", heightClass)}
        role="img"
        aria-label={breakdownAriaLabel}
      >
        {segments.map((seg, i) => {
          const pct = (seg.value / total) * 100;
          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: segment order is fixed per spec render
              key={i}
              className="min-w-0 transition-all"
              style={{
                width: `${pct}%`,
                backgroundColor: segmentColors[i],
              }}
              title={`${seg.label}: ${seg.value}`}
            />
          );
        })}
      </div>
      {(props.showLabels || props.showValues) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {segments.map((seg, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: segment order is fixed per spec render
              key={i}
              className="flex items-center gap-1.5 text-xxs"
            >
              <span
                className="inline-block size-2 shrink-0 rounded-full"
                style={{ backgroundColor: segmentColors[i] }}
                aria-hidden
              />
              {props.showLabels && (
                <span className="text-muted-foreground">{seg.label}</span>
              )}
              {props.showValues && (
                <span className="font-medium">
                  {seg.value} ({Math.round((seg.value / total) * 100)}%)
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HorizontalBar;
